/**
 * Derived line kind (W1-03 core-model-ops; DESIGN.md §6.13, rpe-format.md F§9.5).
 *
 *   kind(line) = line.texture == null
 *                  ? (has paint track ? 'paint' : has text track ? 'text' : 'normal')
 *                  : (texture is a GIF asset && (has gif track || gifAutoLoop) ? 'gif' : 'texture')
 *   uiMover(line) = line.attachUI != null          (orthogonal)
 *
 * A texture counts as a GIF when Phira decodes it with its GIF decoder: the sniffed codec is 'gif' (assets whose
 * codec has not been sniffed, e.g. missing placeholders, fall back to the MIME type / role).
 */

import type { AssetRef, ChartDoc, DocIndexes, Line, LineId, LineKind, LineKindFn } from './types';

/** True when Phira decodes the asset as a GIF (sniffed codec 'gif'; unsniffed: MIME image/gif or role 'gif'). */
export function isGifAsset(asset: AssetRef | undefined): boolean {
  if (asset === undefined) return false;
  const codec = asset.meta?.codec;
  if (codec !== undefined) return codec === 'gif';
  return asset.mime === 'image/gif' || asset.role === 'gif';
}

/** True when the line's texture is a GIF asset (see `isGifAsset`). */
export function hasGifTexture(doc: Pick<ChartDoc, 'assets'>, line: Line): boolean {
  return line.texture !== null && isGifAsset(doc.assets.get(line.texture));
}

/** UI mover: the line drives a HUD element; the line and its notes are not drawn. */
export function isUiMover(line: Line): boolean {
  return line.attachUI !== null;
}

function hasTrack(idx: Pick<DocIndexes, 'tracksOfLine'>, line: LineId, prop: 'paint' | 'text' | 'gif'): boolean {
  const ids = idx.tracksOfLine(line).get(prop);
  return ids !== undefined && ids.length > 0;
}

/** Kind of a line record (no lookup by id). Unknown lines are 'normal'. */
export function kindOfLine(doc: Pick<ChartDoc, 'assets'>, idx: Pick<DocIndexes, 'tracksOfLine'>, line: Line): LineKind {
  if (line.texture === null) {
    if (hasTrack(idx, line.id, 'paint')) return 'paint';
    if (hasTrack(idx, line.id, 'text')) return 'text';
    return 'normal';
  }
  if (isGifAsset(doc.assets.get(line.texture)) && (line.gifAutoLoop || hasTrack(idx, line.id, 'gif'))) return 'gif';
  return 'texture';
}

export const lineKind: LineKindFn = (doc, idx, id) => {
  const line = doc.lines.get(id);
  if (line === undefined) return 'normal';
  return kindOfLine(doc, idx, line);
};
